/**
 * Build status — a temporary window on the app's own progress.
 *
 * **This file is scaffolding and is meant to be deleted**, along with
 * `backend/app/api/routers/status.py`, its router registration, and the nav
 * entry in `AppShell.tsx`. Nothing else imports it.
 *
 * Two kinds of content, kept visibly apart, because mixing them is how a status
 * page starts lying:
 *
 *   * **Counted** — everything numeric comes from `/api/status`, live. A page
 *     about this app's honesty must not be the one place with hardcoded totals.
 *   * **Authored** — phases, decisions and open questions. Prose, dated, and
 *     obviously written by hand.
 *
 * The questions are the reason this page exists rather than a chat log: they
 * are what the build is actually blocked on, and they need to be somewhere the
 * user will see them.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  MessageCircleQuestion,
} from "lucide-react";

import { Badge, Button, Panel, PageHeader } from "@/components/ui/primitives";
import { statusApi, type BuildStatus } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDateTime, formatFiscalYear, formatMoney } from "@/lib/format";

// --- authored content --------------------------------------------------------

/** Bump when the prose below changes, so the page can date itself honestly. */
const CONTENT_UPDATED = "2026-11-04";

type PhaseState = "done" | "current" | "queued";

const PHASES: {
  n: string;
  name: string;
  delivers: string;
  needs: string;
  state: PhaseState;
}[] = [
  {
    n: "0–2",
    name: "Scaffold · Cards · Statement pipeline",
    delivers: "Issuer parsers, review queue, card auto-discovery",
    needs: "—",
    state: "done",
  },
  {
    n: "3",
    name: "Email sync",
    delivers: "IMAP + Gmail API, scheduler, sender routing",
    needs: "—",
    state: "done",
  },
  {
    n: "4",
    name: "Dashboard v1",
    delivers: "Dashboard, card detail, transactions, payment status",
    needs: "—",
    state: "done",
  },
  {
    n: "5",
    name: "Real Cashback Ledger",
    delivers: "FY totals, effective rates, audit export, rules editor",
    needs: "—",
    state: "done",
  },
  {
    n: "5b",
    name: "Benefits split from cashback",
    delivers:
      "Fuel surcharge and fee reversals counted apart; clawbacks netted off",
    needs: "—",
    state: "done",
  },
  {
    n: "6",
    name: "Live transactions from alerts",
    delivers: "Alert parsers for all nine issuers, provisional rows, reconciliation, live cycle spend",
    needs: "—",
    state: "done",
  },
  {
    // Out of band, at your request, before Phase 7 — hence the letter.
    n: "6c",
    name: "Billing cycles",
    delivers:
      "The Cycles page: every card's close and due dates, and how long a purchase today stays interest-free",
    needs: "—",
    state: "done",
  },
  {
    n: "7",
    name: "Caps & purchase planner",
    delivers:
      "“Which card should this go on?” — ranked by what each card has actually paid, with a warning when a card starts paying less than it used to",
    needs: "One real cycle",
    state: "current",
  },
  {
    n: "8",
    name: "Design polish",
    delivers:
      "The Titanium redesign: Geist type, a sidebar, the ⌘K palette, a fanned card deck, due-by-month-end, a cycle clock and purchase-date planner, a cashback card picker, light and dark",
    needs: "Your review of the redesign",
    state: "current",
  },
  {
    n: "9",
    name: "Parser coverage",
    delivers: "Remaining cards, one issuer at a time",
    needs: "Statements for your last unused card",
    state: "queued",
  },
  {
    n: "10",
    name: "Cloud hardening",
    delivers: "Auth on, Postgres verified (suite passes on both), backups, deployment guide",
    needs: "Deploy key on GitHub, DNS for cards.nithishb.com",
    state: "queued",
  },
  {
    n: "M",
    name: "Mobile app",
    delivers:
      "An Android app through NitHub and an iPhone home-screen app: a phone-first redesign, fingerprint lock, notifications, a home-screen widget, sharing a PDF in. M0 (foundations and the fan icon) is done; M1 (phone sign-in, Settings → Devices) is built and waiting for your look",
    needs: "Your phone, for the checks only a real handset can do",
    state: "current",
  },
];

/** What's blocking or would change a number. The reason this page exists. */
const QUESTIONS: { title: string; body: string; weight: "moves" | "whenever" }[] = [
  {
    title: "Look through the redesign on your own data",
    body:
      "Phase 8 is built in the Titanium direction you picked, and your first review is in: the deck is now a fanned hand with every card's bank and last four on its edge, the dashboard has a “Due by month end” figure, Cycles has a cycle clock and a drag-to-pick purchase-date planner, and Cashback filters to any card you pick. Try them on your own data, in both themes and on your phone, and say what feels off — it is still the Phase 8 checkpoint.",
    weight: "moves",
  },
  {
    title: "Let one real cycle run, then look at each card's Rewards tab",
    body:
      "Phase 7 now needs nothing typed in: banks change terms too often, so each card is planned by what it has actually paid, statement by statement. If a card pays clearly less than its usual rate on two statements in a row, the Rewards tab and the card list say so, and the planner switches to the lower rate. After the next statements land, check that the rates and the ranking in “Plan a purchase” look right to you. Rules stay available on each card if you ever want to watch a cap.",
    weight: "moves",
  },
  {
    title: "When the moved bank's first mail arrives, add its new sender",
    body:
      "You've added a forwarding rule for the bank whose mail stopped after 28 Sep. MyDeck's alert reader now recognises its new domain too. The first statement or alert from it will show up in the review queue as an unknown sender — map it to that bank there (Settings → Senders) on this machine before exporting settings, so production knows it as well. Checked 3 Nov: nothing from the new domain has arrived yet. The exact-filter clean-up for the other banks is still in docs/MAIL-FORWARDING.md.",
    weight: "moves",
  },
  {
    title: "Export settings again before the server import",
    body:
      "The ninth card and its PDF password were added on 3 Nov, after the last export-settings run. Without a fresh export, production won't know the card and can't open its statements.",
    weight: "moves",
  },
  {
    title: "Your other unused card is due to be deactivated on 30 Nov",
    body:
      "Its bank wrote in October that a card inactive for 11 months is closed if it isn't used by 30 Nov 2026. That matters only if you want to keep it.",
    weight: "whenever",
  },
  {
    title: "Fuel surcharge rate is observed, not declared",
    body:
      "Your card charges 1% + GST at the pump, which the data confirms to four decimal places. I have not written that rate down anywhere — it stays measured rather than assumed, like every card term now (no terms are typed in).",
    weight: "whenever",
  },
];

/** Things that are true and worth knowing before you find them yourself. */
const GAPS: string[] = [
  "Every issuer now has an alert parser, but most were written from one or two real emails. A parser that looks finished at two samples breaks at seven — the review queue is the safety net until months of live mail have gone through.",
  "On the Cycles page, every close and due date beyond the last statement is projected from that card's own history — hover any of them to see the rule and how many statements agree. Two cards carry an “Estimate” chip because their rule rests on fewer than three.",
  "One card has had no statement since 1 Mar 2026 — but it was paid off to zero and then sat unused, so there was nothing to bill and none was due. The Cycles page distinguishes that from a statement that genuinely should have arrived, and only the second gets a warning.",
  "Repayment emails are read from the three issuers that send them, but they don't mark a bill paid on their own. A statement decides that, so \"mark paid\" is still a manual step.",
  "Most mail from a bank's domains is promotion, not an alert: 415 of the 422 inbox mails no alert wording matched. Those are now ignored. One that reads like money that moved (\"has been debited\", \"payment received\") still goes to review, because it may be a new template. A missed alert costs only the provisional row; the statement still books it.",
  "Live cycle spend is a floor, not a total: banks don't alert on everything, and an authorised amount can settle differently.",
  "Merchant names are only cleaned where a rule matches. Everything else still shows the bank's raw descriptor.",
  "Category editing works in the API but has no control in the transactions table yet.",
  "The effective rate mixes periods slightly: cashback earned in one cycle is credited in the next, so a single year's ratio is approximate at the boundaries.",
  "The Docker image has been built and booted once, in a cloud sandbox, not on the server. Run scripts/serverops-check.sh there before the first deploy.",
  "Production fills from email only, and the inbox starts on 24 Jul 2026. Nothing earlier will be in production. Your settings carry across through export-settings; your data doesn't.",
  "Scapia emails a new PDF password with every statement, so its statements can't be opened automatically. All four in the inbox are locked.",
  "Until the database was widened, every alert would have failed to save on Postgres: Message-ID keys are 70–93 characters, against a 64-character column. All 98 card-bearing alerts in the inbox were affected. Fixed before the first deploy.",
  "Statements don't say whether a purchase was online or in a shop. An online-only cap counts every purchase toward it, so it may have more room than shown — list your online merchants in the rule to make it exact.",
  "Learned rates are per card, not per category: the bank's cashback credit isn't linked to the purchases that earned it.",
  "Milestones can be counted per month or per statement cycle only — not yet per year or card anniversary.",
  "A card whose learned rate rests on a single cashback credit — one card today, from a ₹250 UPI promo in February — is marked Rough on its Rewards tab and in the planner until that credit rolls out of its last six statements. The rate-drop alarm ignores such a card altogether: it needs two earlier statements that paid.",
  "The ninth card's statement reader was built from two statements — one with spend, one settled to zero. Every statement it reads is checked against the bank's own printed summary, so a layout it hasn't seen goes to review instead of booking wrong money.",
];

// --- page --------------------------------------------------------------------

export function StatusPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["build-status"],
    queryFn: () => statusApi.get(),
    refetchInterval: 60_000,
  });

  return (
    <div className="max-w-5xl space-y-8">
      <PageHeader
        eyebrow={<Badge tone="warn">Temporary</Badge>}
        title="Build status"
        description="Live progress while MyDeck is still being built. Every number here is counted from your own data, not written down. This page comes out once the app is stable."
        actions={
          <Button variant="default" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {isError ? (
        <Panel className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-overdue"
              aria-hidden
            />
            <div className="flex-1">
              <p className="text-sm text-ink">Couldn't load the build status.</p>
              <p className="mt-0.5 text-xs text-muted">
                {(error as Error).message}
              </p>
            </div>
            <Button variant="default" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Panel>
      ) : isLoading || !data ? (
        <>
          <Panel className="p-5">
            <div className="skeleton h-3 w-40" />
            <div className="mt-4 grid gap-4 sm:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="skeleton h-8 w-20" />
                  <div className="skeleton mt-2 h-3 w-16" />
                </div>
              ))}
            </div>
          </Panel>
          <Panel className="space-y-2 p-4">
            <div className="skeleton h-9 w-full" />
            <div className="skeleton h-9 w-full" />
            <div className="skeleton h-9 w-full" />
          </Panel>
        </>
      ) : (
        <>
          <Live data={data} />
          <Questions />
          <Phases />
          <Gaps />
        </>
      )}

      <footer className="border-t border-hairline pt-4 text-xs text-faint">
        Numbers counted live from your database. Prose last updated{" "}
        {CONTENT_UPDATED}. Decisions and deviations are logged in{" "}
        <code className="rounded bg-raised px-1 py-0.5">docs/BUILDLOG.md</code>.
      </footer>
    </div>
  );
}

function Live({ data }: { data: BuildStatus }) {
  return (
    <section className="space-y-4">
      <Panel className="p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="label-micro">
            Real cashback · {formatFiscalYear(data.fiscal_year)}
          </div>
          <div className="text-xs text-faint">
            counted {formatDateTime(data.generated_at)}
          </div>
        </div>

        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="This year"
            value={formatMoney(data.real_cashback)}
            hint={
              Number(data.clawbacks) > 0
                ? `after ${formatMoney(data.clawbacks)} clawed back`
                : undefined
            }
            accent
          />
          <Stat
            label="Lifetime"
            value={formatMoney(data.lifetime_real_cashback)}
            hint={`across ${data.fiscal_years.length} fiscal years`}
          />
          <Stat
            label="Benefits"
            value={formatMoney(data.benefits_total)}
            hint="fuel waivers + fee reversals"
          />
          <Stat
            label="Unexplained"
            value={formatMoney(data.unexplained_total)}
            hint={`${data.unexplained_count} credit${data.unexplained_count === 1 ? "" : "s"}, excluded`}
            warn={data.unexplained_count > 0}
          />
        </div>
      </Panel>

      <div className="grid gap-4 sm:grid-cols-2">
        <Panel className="p-5">
          <div className="label-micro">What MyDeck has read</div>
          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Cards" value={`${data.active_cards} active of ${data.cards}`} />
            <Row label="Statements" value={String(data.statements)} />
            <Row label="Transactions" value={String(data.transactions)} />
            <Row
              label="Categorised"
              value={`${data.categorized_transactions} of ${data.transactions}`}
            />
            <Row
              label="Provisional"
              value={
                data.provisional_transactions === 0
                  ? "none live right now"
                  : `${data.provisional_transactions} from alerts`
              }
            />
            <Row
              label="Rules"
              value={`${data.txn_rules} transaction · ${data.merchant_rules} merchant`}
            />
          </dl>
        </Panel>

        <Panel className="p-5">
          <div className="label-micro">Mail &amp; queue</div>
          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Emails processed" value={String(data.emails_processed)} />
            <Row
              label="Alerts parked"
              value={
                data.alerts_pending === 0
                  ? "none — all read"
                  : `${data.alerts_pending} unread`
              }
              warn={data.alerts_pending > 0}
            />
            <Row
              label="Alert parsers"
              value={
                data.alert_parsers.length === 0
                  ? "none yet"
                  : data.alert_parsers.join(" · ")
              }
            />
            <Row
              label="Alert mail unread"
              value={
                (data.review_by_type.alert_parse_failed ?? 0) === 0
                  ? "none"
                  : `${data.review_by_type.alert_parse_failed} sender${
                      data.review_by_type.alert_parse_failed === 1 ? "" : "s"
                    } in review`
              }
              warn={(data.review_by_type.alert_parse_failed ?? 0) > 0}
            />
            <Row
              label="Last sync"
              value={
                data.last_sync_at
                  ? `${formatDateTime(data.last_sync_at)}${data.last_sync_ok === false ? " (failed)" : ""}`
                  : "never"
              }
              warn={data.last_sync_ok === false}
            />
            <Row
              label="Needs review"
              value={
                data.open_review_items === 0
                  ? "nothing"
                  : `${data.open_review_items} item${data.open_review_items === 1 ? "" : "s"}`
              }
              warn={data.open_review_items > 0}
            />
          </dl>

          {data.cards_without_statements.length > 0 && (
            <div className="mt-4 border-t border-hairline pt-3">
              <p className="text-xs text-muted">
                No statement parsed yet —{" "}
                {data.cards_without_statements
                  .map((c) => `${c.name} ••${c.last4}`)
                  .join(", ")}
                . Parser coverage is measured by this list, not by tests passing.
              </p>
            </div>
          )}
        </Panel>
      </div>
    </section>
  );
}

function Questions() {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-display text-base font-medium text-ink">
          What I need from you
        </h2>
        <p className="mt-1 max-w-prose text-sm text-muted">
          Answer any of these in chat. Nothing here is urgent, but the first one
          would move a number.
        </p>
      </div>

      <ul className="space-y-3">
        {QUESTIONS.map((q, i) => (
          <li key={q.title}>
            <Panel
              className={cn(
                "flex gap-4 p-4",
                q.weight === "moves" && "border-brass/30 bg-brass/[0.03]",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "mt-0.5 shrink-0 font-mono text-xs",
                  q.weight === "moves" ? "text-brass" : "text-faint",
                )}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <h3 className="text-sm font-medium text-ink">{q.title}</h3>
                  {q.weight === "moves" ? (
                    <Badge tone="brass">Moves a number</Badge>
                  ) : (
                    <Badge tone="neutral">Whenever</Badge>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted">{q.body}</p>
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </section>
  );
}

const PHASE_ICON = {
  done: CheckCircle2,
  current: CircleDot,
  queued: CircleDashed,
} as const;

function Phases() {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-base font-medium text-ink">
        The road to spec-complete
      </h2>

      <Panel className="overflow-hidden">
        <ul className="divide-y divide-hairline">
          {PHASES.map((p) => {
            const Icon = PHASE_ICON[p.state];
            return (
              <li
                key={p.n}
                className={cn(
                  "flex items-start gap-3 px-5 py-3",
                  p.state === "current" && "bg-brass/[0.04]",
                )}
              >
                <Icon
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0",
                    p.state === "done" && "text-done",
                    p.state === "current" && "text-brass",
                    p.state === "queued" && "text-faint",
                  )}
                  aria-hidden
                />
                <span className="w-8 shrink-0 pt-px font-mono text-xs text-faint">
                  {p.n}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink">{p.name}</div>
                  <div className="mt-0.5 text-xs text-muted">{p.delivers}</div>
                </div>
                <div className="hidden w-40 shrink-0 pt-px text-xs text-faint sm:block">
                  {p.needs === "—" ? (
                    <span className="text-done">signed off</span>
                  ) : (
                    p.needs
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Panel>
    </section>
  );
}

function Gaps() {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageCircleQuestion className="h-4 w-4 text-muted" aria-hidden />
        <h2 className="font-display text-base font-medium text-ink">
          Open gaps, stated plainly
        </h2>
      </div>
      <Panel className="p-5">
        <ul className="space-y-2 text-sm text-muted">
          {GAPS.map((g) => (
            <li key={g} className="flex gap-2.5">
              <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-strong" />
              <span>{g}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  accent,
  warn,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div>
      <div className="label-micro">{label}</div>
      <div
        className={cn(
          "mt-1.5 font-display text-figure font-semibold tabular-nums",
          accent ? "text-brass" : warn ? "text-warn" : "text-ink",
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </div>
  );
}

function Row({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted">{label}</dt>
      {/* Wraps rather than shrinks. Numbers never need it; the list of alert
          parsers overran the panel the moment it reached eight issuers. */}
      <dd
        className={cn(
          "min-w-0 text-right tabular-nums",
          warn ? "text-warn" : "text-ink",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
